import { useEffect, useState, useCallback } from 'react';
import { api } from '../api';
import ExportButton from '../components/ExportButton';

// API 超时保护：防止后端挂起导致浏览器一直等待
function fetchWithTimeout(url: string, ms = 8000) {
  return Promise.race([
    api(url),
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('请求超时')), ms)),
  ]);
}

export default function Products() {
  const [list, setList] = useState<any[]>([]);
  const [stats, setStats] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState<any>(null);
  const [form, setForm] = useState({ code: '', name: '', category: '', unit: '', price: '', cost: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      fetchWithTimeout('/products').then(setList).catch(() => setList([])),
      fetchWithTimeout('/products/sales-stats').then(setStats).catch(() => setStats([])),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.price) return;
    if (edit) {
      await api('/products/' + edit.id, { method: 'PUT', body: JSON.stringify({ ...form, price: Number(form.price), cost: Number(form.cost) || 0 }) });
    } else {
      await api('/products', { method: 'POST', body: JSON.stringify({ ...form, price: Number(form.price), cost: Number(form.cost) || 0 }) });
    }
    setShow(false); setEdit(null); setForm({ code: '', name: '', category: '', unit: '', price: '', cost: '' }); load();
  };

  const del = async (id: number) => {
    if (!confirm('确认删除该产品？')) return;
    await api('/products/' + id, { method: 'DELETE' }); load();
  };

  const openEdit = (r: any) => { setEdit(r); setForm({ code: r.code || '', name: r.name, category: r.category || '', unit: r.unit || '', price: String(r.price), cost: String(r.cost || '') }); setShow(true); };

  return (
    <div className="space-y-4">
      {/* 加载状态：防止白屏/卡死无反馈 */}
      {loading && (
        <div className="text-center py-8 text-slate-400">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-brand-600 border-t-transparent"></div>
          <div className="mt-2 text-sm">加载产品数据中...</div>
        </div>
      )}
      {/* 错误状态：防止静默失败 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-3 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={load} className="underline text-sm hover:text-red-800">重试</button>
        </div>
      )}

      {!loading && !error && (
      <>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold">产品管理</h2>
        <div className="flex gap-2">
          <ExportButton
            filename="产品清单"
            fetchData={() => api('/products')}
            columns={[
              { header: '编码', key: 'code' },
              { header: '名称', key: 'name' },
              { header: '分类', key: 'category' },
              { header: '单位', key: 'unit' },
              { header: '售价', key: 'price' },
              { header: '成本', key: 'cost' },
              { header: '利润率', key: 'price', format: (r) => (Number(r.price) > 0 && Number(r.cost) > 0 ? ((1 - Number(r.cost) / Number(r.price)) * 100).toFixed(1) + '%' : '—') },
            ]}
          />
          <button onClick={() => { setEdit(null); setForm({ code: '', name: '', category: '', unit: '', price: '', cost: '' }); setShow(true); }} className="bg-brand-600 text-white rounded-lg px-4 py-2 hover:bg-brand-700">
            + 新增产品
          </button>
        </div>
      </div>

      {/* 销量排行 */}
      {stats.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4">
          <h3 className="font-bold mb-3 text-slate-700">📊 产品销量排行</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {stats.slice(0, 6).map((s, i) => (
              <div key={s.id} className={`rounded-lg p-3 border ${i === 0 ? 'border-yellow-400 bg-yellow-50' : 'border-slate-200'}`}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">{s.name}</span>
                  <span className="text-xs text-slate-500">#{i + 1}</span>
                </div>
                <div className="mt-1 text-sm text-slate-600">销量: <b>{Number(s.total_qty || 0)}</b> | 销售额: <b className="text-green-600">¥{Number(s.total_sales || 0).toLocaleString()}</b></div>
                <div className="text-xs text-slate-400">利润: ¥{Number(s.profit || 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 产品列表 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-left">
              <th className="p-3">编码</th><th>名称</th><th>分类</th><th>单位</th><th>售价</th><th>成本</th><th>利润率</th><th className="w-28">操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-mono text-xs">{p.code || '—'}</td>
                <td className="p-3 font-medium">{p.name}</td>
                <td className="p-3"><span className="px-2 py-0.5 bg-slate-100 rounded text-xs">{p.category || '—'}</span></td>
                <td className="p-3">{p.unit || '—'}</td>
                <td className="p-3 text-green-600 font-medium">¥{Number(p.price).toLocaleString()}</td>
                <td className="p-3 text-red-500">¥{Number(p.cost || 0).toLocaleString()}</td>
                <td className="p-3"><span className={Number(p.price) > 0 && Number(p.cost) > 0 ? 'text-blue-600' : 'text-slate-400'}>{Number(p.price) > 0 && Number(p.cost) > 0 ? ((1 - Number(p.cost) / Number(p.price)) * 100).toFixed(1) + '%' : '—'}</span></td>
                <td className="p-3 space-x-2">
                  <button onClick={() => openEdit(p)} className="text-brand-600 hover:underline text-xs">编辑</button>
                  <button onClick={() => del(p.id)} className="text-red-500 hover:underline text-xs">删除</button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">暂无产品，点击右上角新增</td></tr>}
          </tbody>
        </table>
      </div>

      {/* 弹窗 */}
      {show && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShow(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">{edit ? '编辑产品' : '新增产品'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <input className="border rounded-lg px-3 py-2" placeholder="产品编码" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" placeholder="产品名称 *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input className="border rounded-lg px-3 py-2" placeholder="分类" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" placeholder="单位（个/件/把）" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" type="number" placeholder="售价 *" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
              <input className="border rounded-lg px-3 py-2" type="number" placeholder="成本价" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShow(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={save} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">保存</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
